import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import { KetMcqQuestion } from './KetReadingPart2';
import { ketNumberMap } from './ketNumbering';

// A2 Key for Schools (2020 format, "ket2") — shared MCQ renderer for the
// reading parts that KetReadingPart2's plain card cannot cover on its own:
//   Part 1: one sign/notice image per question (content_data.question_images,
//           indexed by question order_index) above the A/B/C options
//   Part 2: shared reading panel as an image (content_data.image_url)
//   Part 3: shared reading passage as text (content_data.reading_text)
const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

const Ket2ReadingMCQ = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error('Failed to parse saved attempt from localStorage', e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;
    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }
    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });
    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => saveAnswersToLocalStorage(answers), 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const { image_url, reading_text, story_title, question_images } = exerciseData.content_data;
  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const numberMap = ketNumberMap(exerciseData);

  const handleSelect = (questionId, optionId) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  };

  const questionImageFor = (q) =>
    Array.isArray(question_images) && question_images[q.order_index]
      ? `${ASSET_BASE_URL}${question_images[q.order_index]}`
      : null;

  return (
    <div className="w-full h-full p-1 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="max-w-3xl mx-auto w-full py-4 space-y-3">
        {/* Shared reading panel — image scan or typed passage */}
        {(image_url || reading_text) && (
          <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-sm p-4">
            {story_title && (
              <h3 className="font-display font-black text-lg text-slate-800 dark:text-white text-center mb-3">
                {story_title}
              </h3>
            )}
            {image_url ? (
              <img
                src={`${ASSET_BASE_URL}${image_url}`}
                alt={story_title || 'reading'}
                className="w-full h-auto rounded-xl"
              />
            ) : (
              <p className="font-body text-base text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-line m-0">
                {reading_text}
              </p>
            )}
          </div>
        )}

        {sortedQuestions.map((q) => (
          <div key={q.id}>
            {questionImageFor(q) && (
              <div className="bg-white rounded-t-2xl border-2 border-b-0 border-slate-100 px-4 pt-4 pb-1 flex justify-center">
                <img
                  src={questionImageFor(q)}
                  alt={`question ${numberMap[q.id] || ''}`}
                  className="max-h-56 w-auto rounded-lg"
                />
              </div>
            )}
            <KetMcqQuestion
              question={q}
              questionNumber={numberMap[q.id]}
              answer={answers[q.id]}
              onSelect={handleSelect}
              showHint={showHint}
              isReviewMode={isReviewMode}
              isHydrated={isHydrated}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Ket2ReadingMCQ;
